import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { Card } from "primereact/card";
import { Button } from "primereact/button";

import { themeClasses, C } from "sj-ui-toolkit/src/atlas/utils";
import { ContentTag, SummaryTopicCard } from "sj-ui-toolkit/src/atlas/components";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { getKnowledgeAssessmentReportClient } from "sj-ui-toolkit/src/client";
import { getProctorClient } from "sj-ui-toolkit/src/client/svcProctor";
import { humanDateWithoutTime, currentBalanceDate } from "sj-ui-toolkit/src/utils/dates";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";

import {
  calculateAverageScores,
  enrichLearnerRows,
  filterAllScores,
} from "@/helpers/assessmentsReport";
import {
  type LearnerScore,
  type CategoryScore,
  type TopicScore,
} from "@/constants/assessmentsReport.constants";
import QuestionsResult, {
  type ExamResult,
} from "@/components/UserAssessmentSummary/QuestionsResult";
import UserAssessmentSummarySkeleton from "@/components/UserAssessmentSummary/UserAssessmentSummarySkeleton";

// ---------------------------------------------------------------------------
// Helper: format seconds to "Xm Ys"
// ---------------------------------------------------------------------------

const formatTimeSpent = (timeSpent: string | number | null | undefined): string => {
  if (timeSpent == null || timeSpent === "") return "N/A";
  const totalSeconds = parseFloat(String(timeSpent));
  if (isNaN(totalSeconds) || totalSeconds <= 0) return "N/A";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}m ${seconds}s`;
};

const SELF_INITIATED_ID = "self-initiated";
const SELF_INITIATED_EXAM_KEY = "the-exam";

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function AssessmentReportUser() {
  const { id: assessmentId, user_id: userId } = useParams<{ id: string; user_id: string }>();
  const [searchParams] = useSearchParams();
  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const { showToast } = useToast();
  const hideTimeSpent = useHideTimeSpent();

  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<Partial<LearnerScore>>({});
  const [assessmentTitle, setAssessmentTitle] = useState("");
  const [userScores, setUserScores] = useState<
    Array<{ category: string; topic: string; score: number; possiblePoints: number }>
  >([]);
  const [examResult, setExamResult] = useState<ExamResult[]>([]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number | number[]>([]);
  const [expandedSummaries, setExpandedSummaries] = useState<number[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!assessmentId || !userId || !currentTenantName) return;

    setIsLoading(true);
    try {
      const isSelfInitiated = assessmentId === SELF_INITIATED_ID;
      const assessmentResponse = await getKnowledgeAssessmentReportClient().getScores(
        currentTenantName,
        isSelfInitiated
          ? { examKey: SELF_INITIATED_EXAM_KEY, userInitiated: true }
          : { assignmentId: assessmentId }
      );
      if (assessmentResponse.error) throw assessmentResponse.error;

      const allAssignmentScores =
        (
          assessmentResponse.data as {
            assignmentScores?: Array<{
              assignmentTitle?: string;
              learnerScores?: Array<Record<string, unknown>>;
            }>;
          }
        )?.assignmentScores ?? [];

      const allLearners = allAssignmentScores.flatMap(a => a.learnerScores ?? []);
      const examInstanceId = isSelfInitiated ? searchParams.get("examInstanceId") : null;

      const rawLearnerScore = (
        examInstanceId
          ? allLearners.find(l => l.examInstanceId === examInstanceId)
          : allLearners.find(l => l.directoryUuid === userId)
      ) as LearnerScore | undefined;

      const assignmentTitle = isSelfInitiated
        ? "Self-initiated Assessment"
        : (allAssignmentScores[0]?.assignmentTitle ?? "");

      if (!rawLearnerScore) {
        throw new Error("User not found in assessment data");
      }

      const [enriched] = enrichLearnerRows([rawLearnerScore]);

      // Get exam results if examInstanceId exists
      let examResults: ExamResult[] = [];
      if (rawLearnerScore.examInstanceId) {
        try {
          const { data, error } = await getProctorClient().viewExam(rawLearnerScore.examInstanceId);
          if (error) throw error;
          examResults = ((data as unknown as { results?: ExamResult[] })?.results ??
            []) as ExamResult[];
        } catch (err) {
          const isNoRows = (err as { message?: string })?.message === "no rows in result set";
          if (!isNoRows) throw err;
        }
      }

      // Filter applicable scores for the category/topic breakdown
      const filteredScores = filterAllScores([rawLearnerScore]);

      setAssessmentTitle(assignmentTitle);
      setUserData(enriched ?? rawLearnerScore);
      setUserScores(filteredScores);
      setExamResult(examResults);
    } catch (error) {
      console.error("Error fetching assessment user data:", error);
      showToast({ severity: "error", detail: "Failed to load assessment data" });
    } finally {
      setIsLoading(false);
    }
  }, [assessmentId, userId, currentTenantName, searchParams, showToast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const { categoryScoresAverage, topicScoresAverage } = useMemo(
    () =>
      userScores.length > 0
        ? calculateAverageScores(userScores)
        : {
            categoryScoresAverage: [] as CategoryScore[],
            topicScoresAverage: {} as Record<string, TopicScore[]>,
          },
    [userScores]
  );

  const displayName =
    userData.familyName && userData.givenName
      ? `${userData.familyName}, ${userData.givenName}`
      : (userData.email ?? "");

  useBreadcrumb(
    displayName
      ? {
          label: displayName,
          route: `/admin/reports/assessments/${assessmentId ?? ""}/users/${userId ?? ""}`,
          display: "page",
        }
      : null
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handlePrint = useCallback(() => {
    setPdfLoading(true);
    const prevActiveQuestion = activeQuestionIndex;
    setActiveQuestionIndex(examResult.map((_, index) => index));
    setExpandedSummaries(categoryScoresAverage.map((_, index) => index));
    setTimeout(() => {
      window.print();
      setPdfLoading(false);
      setActiveQuestionIndex(prevActiveQuestion);
      setExpandedSummaries([]);
    }, 500);
  }, [activeQuestionIndex, examResult, categoryScoresAverage]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return <UserAssessmentSummarySkeleton />;
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      {/* Left Panel - User & Assessment Info */}
      <div className="col-span-1 mt-1 print:hidden">
        <div className="flex flex-col gap-6">
          <ContentTag value="assessment" className={themeClasses.bgElevation2} />
          <div className="flex flex-col gap-2">
            <h5 className="atlas-h5 text-surface-600">User</h5>
            <p className="atlas-h4 break-all">{displayName}</p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="relative col-span-2 print:col-span-3">
        <Card
          className={`${themeClasses.bgElevation2} atlas-bg-summary -mr-10 rounded-tl-2xl relative flex flex-col min-h-[calc(100vh-5rem)] print:min-h-0 print:h-auto print:overflow-visible`}
          style={{ backgroundRepeat: "no-repeat" }}
        >
          <div className="flex-shrink-0">
            <ContentTag
              value="assessment"
              className={C(themeClasses.bgElevation2, "hidden print:block print:mb-4")}
            />
            <div className="flex justify-between items-center mb-8 gap-6">
              <h1>{assessmentTitle}</h1>
              {userScores.length > 0 && (
                <Button
                  severity="secondary"
                  icon={pdfLoading ? "pi pi-spin pi-spinner" : "pi pi-download"}
                  label="Download PDF"
                  onClick={handlePrint}
                  className="print:hidden"
                />
              )}
              {/* Print-only display name */}
              <div className="hidden print:block">
                <h5 className="atlas-h5 text-surface-600">User</h5>
                <p className="atlas-h4">{displayName}</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-10">
              <div className="flex flex-col gap-1">
                <h5 className="atlas-h5 text-secondary">Demonstrated Proficiency</h5>
                <span className="atlas-h3 capitalize">{userData.proficiency ?? "N/A"}</span>
                <span className="block body-sm text-secondary">
                  {userData.averageScore != null ? `${userData.averageScore}%` : "N/A"} score
                </span>
              </div>
              {!hideTimeSpent && (
                <div className="flex flex-col gap-1">
                  <h5 className="atlas-h5 text-secondary">Time to Complete</h5>
                  <span className="atlas-h3">{formatTimeSpent(userData.timeSpent)}</span>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <h5 className="atlas-h5 text-secondary">Date Completed</h5>
                <span className="atlas-h3">
                  {userData.completedAt ? humanDateWithoutTime(userData.completedAt) : "N/A"}
                </span>
                <span className="block body-sm text-secondary">
                  Due at {userData.dueDate ? currentBalanceDate(userData.dueDate) : "N/A"}
                </span>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="space-y-12 mb-12 print:mb-0 print:break-after-page">
              {categoryScoresAverage.map((categoryScore, index) => (
                <SummaryTopicCard
                  key={categoryScore.category}
                  categoryScore={categoryScore}
                  topicScoresAverage={topicScoresAverage}
                  expanded={expandedSummaries.includes(index)}
                />
              ))}
            </div>
          </div>

          <QuestionsResult
            results={examResult}
            activeIndex={activeQuestionIndex}
            setActiveIndex={setActiveQuestionIndex}
          />
        </Card>
      </div>
    </div>
  );
}
